/* eslint-disable camelcase */
import axios from 'axios';
import type { AddonWhitelist } from '@tencent/td-addon-sdk-docs';

const TIME_GAP = 1000 * 60 * 60;
const VIDEO = '视频';

export const URL_REQUEST_ABORT = 'abort';
export default class PicUrlCacheManager {
    private static ins: PicUrlCacheManager | null = null;

    public static getInstance() {
        if (!PicUrlCacheManager.ins) {
            PicUrlCacheManager.ins = new PicUrlCacheManager();
        }
        return PicUrlCacheManager.ins;
    }
    // 缓存picUrl的map
    private picUrlCacheMap: Map<string, any>;
    private readonly docsAddonSdk: Promise<any>;

    public constructor() {
        this.picUrlCacheMap = new Map<string, any>();
        this.docsAddonSdk = import(/* webpackChunkName: "addon" */ '@tencent/td-addon-sdk-docs');
    }

    /**
     * 往缓存里设置kv
     * @param k md5Key
     * @param url picUrl
     */
    public setCache = (k, url) => {
        const v = {
            url,
            timeStamp: new Date(),
        };
        this.picUrlCacheMap.set(k, v);
    };

    /**
     * 检查缓存中是否存在值,并且未超过一小时
     * @param k md5key
     */
    public existInCache = (k) => {
        const value = this.picUrlCacheMap.get(k);
        if (value) {
            return Date.now() - value.timeStamp.getTime() < TIME_GAP;
        }
        return false;
    };

    /**
     * 根据插件url向后台获取截图
     * @param addonInfo
     * @param md5Key 前两个的md5
     * @param addonManager 引用manager的方法去拿类型type
     */
    public getScreenshotImage = async (addonInfo: any, md5Key: string, width: number, height: number): Promise<any> => {
        const { originUrl, embedUrl, extraData } = addonInfo;
        // 这里首先判断下是否有缓存
        if (this.existInCache(md5Key)) {
            return Promise.resolve(this.picUrlCacheMap.get(md5Key).url);
        }

        const resourceOriginType = (await this.getOriginTypeFromUrl(extraData)) || 0;
        const globalPadId: string = window.clientVars?.globalPadId;
        return axios('/v2/file/screenshot', {
            headers: {
                'Content-Type': 'application/json; charset=UTF-8',
            },
            method: 'POST',
            data: {
                origin_url: originUrl,
                embed_url: embedUrl,
                global_pad_id: globalPadId,
                origin_type: resourceOriginType,
                pic_width: Math.floor(width),
                pic_height: Math.floor(height),
            },
        }).then((res) => {
            const { data } = res;
            if (data?.msg === 'success') {
                const picUrl = data?.result.picUrl;
                this.setCache(md5Key, picUrl);
                return picUrl;
            }
            // 正确返回picUrl 错误返回res整个对象 error在逻辑中捕捉
            return res;
        });
    };

    /**
     * 根据url判断出对应的类型作为参数传给后台
     * @param url
     * @param iframeId
     * @param addonManager
     */
    public getOriginTypeFromUrl = async (extraData: string) => {
        if (extraData) {
            const { type } = JSON.parse(extraData);
            return Number(type);
        }
    };
    /**
     * 根据id与url判断是否为视频类型
     * @param addon 插件数据
     * @param addonManager manager方法
     */
    public isVideoType = async (addon, addonManager) => {
        const { iframeId, extraData } = addon;
        const type = await PicUrlCacheManager.getInstance()
            .getOriginTypeFromUrl(extraData);
        const addonInfoMenus = addonManager.addonHandler.addonMetaMap.get(iframeId)?.config?.menus || [];
        return addonInfoMenus.some(item => Number(item?.report_id) === type && item.category === VIDEO);
    };

    // 根据 link 获取插件 icon
    public getIconOfAddonLink = async (addonId: string, url: string): Promise<string> => {
        const { DocAddonBridge } = await this.docsAddonSdk;
        let addonIcon;
        try {
            addonIcon = await DocAddonBridge.getIconOfAddonLink(url, addonId);
        } catch (error) {
            window.log?.error(error);
        }
        return addonIcon;
    };

    /**
     * 根据插件 Id 和 extraData 获取插件名
     * @param addonId 插件 Id
     * @param extraData 插件附加数据
     */
    public getAddonName = async (addonId: string, extraData: string): Promise<string> => {
        const { DocAddonBridge } = await this.docsAddonSdk;
        const type = DocAddonBridge.getLinkType(addonId, extraData);
        return DocAddonBridge.getLinkTypeInfo(type).title;
    };

    // 获取小程序渲染插件 iframe 的白名单
    public getMiniAppIframeWhiteList = async (): Promise<AddonWhitelist[]> => {
        const { DocAddonBridge } = await this.docsAddonSdk;
        const miniAppRenderWhitelist = await DocAddonBridge.getMiniAppIframeWhiteList();
        return miniAppRenderWhitelist;
    };
}
